import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import './scss/worktualtab.scss';

interface Tab {
  label: string;
  content: React.ReactNode;
  removeTopSpace?:boolean
}

// interface TabsProps {
//   tabs: Tab[];
//   activeTab: number;                        // controlled index
//   onTabChange: (index: number) => void;     // controlled change handler
// }

interface TabsProps {
  tabs: Tab[];
  /** Controlled mode */
  activeTab?: number;
  onTabChange?: (index: number) => void;
  /** Uncontrolled mode */
  activeTabIndex?: number;
  className?: string;
}

const isScrollable = (el: Element | null) => {
  if (!el || !(el instanceof HTMLElement)) return false;
  const style = getComputedStyle(el);
  const overflowY = style.overflowY;
  const overflow = style.overflow;
  const scrollable = /(auto|scroll)/.test(overflowY + overflow);
  return scrollable && el.scrollHeight > el.clientHeight;
};

const findScrollableAncestor = (el: Element | null): Element | null => {
  let cur = el;
  while (cur) {
    if (isScrollable(cur)) return cur;
    cur = cur.parentElement;
  }
  return null;
};

const scrollToTop = (el: Element | Window | null, smooth = false) => {
  try {
    if (!el) return;
    if (el instanceof Window) {
      window.scrollTo({ top: 0, behavior: smooth ? "smooth" : "auto" });
    } else if (el instanceof HTMLElement) {
      // prefer instant to avoid race; set smooth if desired
      el.scrollTo({ top: 0, behavior: smooth ? "smooth" : "auto" });
      // fallback to setting scrollTop for older browsers
      el.scrollTop = 0;
    }
  } catch (err) {
    // last resort
    window.scrollTo(0, 0);
  }
};


const WorktualTab: React.FC<TabsProps> = ({ tabs, activeTab, onTabChange, activeTabIndex, className }) => {
  const [internalActiveTab, setInternalActiveTab] = useState(activeTabIndex ?? 0);

  // use controlled if activeTab is provided, otherwise fallback to internal
  const currentTab = activeTab !== undefined ? activeTab : internalActiveTab;

  const handleTabSwitch = (index: number) => {
    if (activeTab !== undefined && onTabChange) {
      // controlled mode
      if (document.startViewTransition) {
        document.startViewTransition(() => onTabChange(index));
      } else {
        onTabChange(index);
      }
    } else {
      // uncontrolled mode
      setInternalActiveTab(index);
    }
  };

  const contentRef = useRef<HTMLDivElement | null>(null);
   useLayoutEffect(() => {
    // debug:
    // console.log("currentTab changed to", currentTab, "contentRef:", contentRef.current);

    // If wrapper itself is scrollable, scroll it
    if (contentRef.current && isScrollable(contentRef.current)) {
      scrollToTop(contentRef.current, false); // set true for smooth
      return;
    }

    // otherwise find the nearest scrollable ancestor (could be a parent container)
    const scrollableAncestor = findScrollableAncestor(contentRef.current);
    if (scrollableAncestor) {
      scrollToTop(scrollableAncestor, false);
      return;
    }

    // fallback to window (page scroll)
    scrollToTop(window, false);
  }, [currentTab]);
  return (
    <div className={`wttabview ${className ?? ''}`.trim()}>
      <div className={`wttabheaderouter ${tabs[currentTab]?.removeTopSpace ? 'wttabheaderouternospace' : ''}`}>
        <div className="wttabheader">
          {tabs.map((tab, index) => (
            <span
              key={index}
              onClick={() => handleTabSwitch(index)}
              className={`wttab ${currentTab === index ? 'wtactivetab' : ''}`}
            >
              {tab.label}
            </span>
          ))}
        </div>
      </div>
      <div className="wttabcontent" ref={contentRef}>{tabs[currentTab].content}</div>
    </div>
  );
};



export default WorktualTab;
